import React, { useState, useEffect, useRef } from "react";
import { Column, Row, Text, Button, Input, Modal } from "@amzn/meridian";
import { setShowWorkflowHeader } from "src/store/workflowSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

interface ContainerCountStepProps {
  humanText: string;
  onComplete: (data: { drumCount: number }) => void;
}

const ContainerCountStep: React.FC<ContainerCountStepProps> = ({
  humanText,
  onComplete,
}) => {
  const dispatch = useAppDispatch();
  const { flags } = useAppSelector((state) => state.featureFlags);
  const onCompleteRef = useRef(onComplete);
  
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // TODO: Remove ops_live feature flag conditional logic https://taskei.amazon.dev/tasks/V2160357274
    // When opsLive is enabled, skip the container count prompt and auto-advance with drumCount=2
    if (flags.opsLive) {
      dispatch(setShowWorkflowHeader(true));
      onCompleteRef.current({ drumCount: 1 });
      return;
    }
    dispatch(setShowWorkflowHeader(false));
  }, [flags.opsLive, dispatch]);

  const [drumCount, setDrumCount] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleSubmit = () => {
    const count = parseInt(drumCount, 10);
    
    if (isNaN(count) || count <= 0) {
      setError("Please enter a valid number greater than 0");
      return;
    }

    if (count > 100) {
      setError("Please enter a number less than or equal to 100");
      return;
    }
    dispatch(setShowWorkflowHeader(true));
    onComplete({ drumCount: count });
  };

  const handleInputChange = (value: string) => {
    setDrumCount(value);
    setError("");
  };

  return (
    <Modal open={true} title="Container Count">
      <Column spacing="400">
        <Text>{humanText}</Text>
        
        <Input
          value={drumCount}
          onChange={(value) => handleInputChange(value)}
          type="number"
          placeholder="Enter number of containers"
        />
        
        {error && (
          <Text type="b200" color="error">
            {error}
          </Text>
        )}

        <Row alignmentHorizontal="end">
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!drumCount}
          >
            Continue
          </Button>
        </Row>
      </Column>
    </Modal>
  );
};

export default ContainerCountStep;
